/**
 * CashRegisterTransactionPendingController — page
 * `/caisse-transaction-en-attente.html?id=<uuid>[&returnTo=close]`
 *
 * EPIC-043 / 43.5 — Enregistrement d'une vente nominative (cash
 * individuel avec `client_name`, §7.J.D) :
 *   - charge le libellé de la session via GET /sessions/{id} ;
 *   - hint dynamique : « obligatoire » si `amount ≥ 76 €` (BOI-TVA-DECLA-
 *     30-10-30 / art. 286-I-3° CGI / livre des recettes) ;
 *   - validation côté client : `amount > 0`, `clientName` obligatoire
 *     si `amount ≥ 76 €` ;
 *   - POST /v1/cash-register/sessions/{id}/transactions
 *     `{amount, notes?, clientName?}` ;
 *   - 201 → redirection vers la fiche session (ou la page de clôture si
 *     `?returnTo=close` était présent) ;
 *   - 422 → erreurs sous les champs ;
 *   - 409 → banner critique « session déjà clôturée ».
 *
 * Convention `frontend/CLAUDE.md` : aucun HTML construit en JS.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const THRESHOLD_CENTS = 7600; // 76,00 €

export default class CashRegisterTransactionPendingController extends BaseController {
  static targets = [
    "sessionCrumb",
    "sessionSubtitle",
    "errorBanner",
    "amountInput",
    "amountError",
    "clientNameInput",
    "clientNameError",
    "clientNameRequiredMark",
    "clientNameHint",
    "notesInput",
    "notesError",
    "cancelLink",
    "submitBtn",
  ];

  connect() {
    this._submitting = false;
    this._sessionId = this._extractSessionId();
    this._returnTo = this._extractReturnTo();
    if (!this._sessionId) {
      this._showErrorBanner("Session introuvable", "Identifiant de session manquant dans l'URL.");
      return;
    }
    this._renderStaticLinks();
    this._fetchSubtitle();
    this.toggleClientNameRequired();
  }

  _extractSessionId() {
    const params = new URLSearchParams(window.location.search);
    return params.get("id") || null;
  }

  _extractReturnTo() {
    const params = new URLSearchParams(window.location.search);
    return params.get("returnTo") || null;
  }

  _renderStaticLinks() {
    const idEnc = encodeURIComponent(this._sessionId);
    if (this.hasSessionCrumbTarget) {
      this.sessionCrumbTarget.setAttribute("href", `/caisse-session.html?id=${idEnc}`);
    }
    if (this.hasCancelLinkTarget) {
      // 99.19 — `returnTo=close` ramène désormais directement sur l'ancre
      // `#close-section` de la fiche session (page `caisse-cloture.html`
      // supprimée, clôture intégrée en bloc inline).
      const back = this._returnTo === "close"
        ? `/caisse-session.html?id=${idEnc}#close-section`
        : `/caisse-session.html?id=${idEnc}`;
      this.cancelLinkTarget.setAttribute("href", back);
    }
  }

  async _fetchSubtitle() {
    try {
      const response = await api.cashRegister.getSession(this._sessionId);
      if (!this.hasSessionSubtitleTarget) return;
      // Ressource enveloppée dans `{data: {...}}` côté backend.
      const session = response?.data ?? response;
      const dateLabel = this._formatDate(session.sessionDate);
      const label = session.label ? `${session.label} · ${dateLabel}` : dateLabel;
      this.sessionSubtitleTarget.textContent = `Session : ${label}`;
    } catch (error) {
      // Non bloquant : le sous-titre reste la valeur statique de la maquette.
    }
  }

  toggleClientNameRequired() {
    const raw = this.hasAmountInputTarget ? this.amountInputTarget.value : "";
    const amount = Number(raw);
    const cents = Number.isFinite(amount) ? Math.round(amount * 100) : 0;
    const required = cents >= THRESHOLD_CENTS;
    if (this.hasClientNameRequiredMarkTarget) {
      this.clientNameRequiredMarkTarget.hidden = !required;
    }
    if (this.hasClientNameHintTarget) {
      this.clientNameHintTarget.textContent = required
        ? "Obligatoire : le montant est ≥ 76 € TTC (livre des recettes)."
        : "Requis si le montant est ≥ 76 € ; optionnel sinon.";
    }
  }

  async submit(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    if (this._submitting) return;
    this._clearFieldErrors();
    this._hideErrorBanner();

    const amount = this.hasAmountInputTarget ? Number(this.amountInputTarget.value) : NaN;
    const clientName = this.hasClientNameInputTarget ? this.clientNameInputTarget.value.trim() : "";
    const notes = this.hasNotesInputTarget ? this.notesInputTarget.value.trim() : "";

    let hasError = false;
    if (!Number.isFinite(amount) || amount <= 0) {
      this._showFieldError("amountError", "Le montant doit être supérieur à 0.");
      hasError = true;
    }
    if (
      Number.isFinite(amount)
      && Math.round(amount * 100) >= THRESHOLD_CENTS
      && clientName === ""
    ) {
      this._showFieldError(
        "clientNameError",
        "Le nom du client est obligatoire pour les ventes ≥ 76 € TTC.",
      );
      hasError = true;
    }
    if (hasError) return;

    const payload = { amount: amount.toFixed(2) };
    if (clientName !== "") payload.clientName = clientName;
    if (notes !== "") payload.notes = notes;

    this._setSubmitting(true);
    try {
      await api.cashRegister.addPendingTransaction(this._sessionId, payload);
      const idEnc = encodeURIComponent(this._sessionId);
      const back = this._returnTo === "close"
        ? `/caisse-session.html?id=${idEnc}#close-section`
        : `/caisse-session.html?id=${idEnc}`;
      window.location.assign(back);
    } catch (error) {
      this._setSubmitting(false);
      const status = error instanceof APIError ? error.status : 0;
      if (status === 409) {
        this._showErrorBanner(
          "Session déjà clôturée",
          "Vous ne pouvez plus enregistrer de vente sur cette session.",
        );
      } else if (status === 422) {
        this._applyFieldErrors(error);
      } else {
        const msg = (error instanceof APIError && error.message) || "Impossible d'enregistrer la vente.";
        this._showErrorBanner("Erreur", msg);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _setSubmitting(submitting) {
    this._submitting = submitting;
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = submitting;
      this.submitBtnTarget.setAttribute("aria-busy", submitting ? "true" : "false");
    }
  }

  _showErrorBanner(title, message) {
    if (!this.hasErrorBannerTarget) {
      this.showError(`${title} — ${message}`);
      return;
    }
    const titleEl = this.errorBannerTarget.querySelector('[data-slot="error-title"]');
    const msgEl = this.errorBannerTarget.querySelector('[data-slot="error-message"]');
    if (titleEl) titleEl.textContent = title;
    if (msgEl) msgEl.textContent = " " + message;
    this.errorBannerTarget.hidden = false;
  }

  _hideErrorBanner() {
    if (this.hasErrorBannerTarget) this.errorBannerTarget.hidden = true;
  }

  _clearFieldErrors() {
    for (const name of ["amountError", "clientNameError", "notesError"]) {
      const has = "has" + name.charAt(0).toUpperCase() + name.slice(1) + "Target";
      if (this[has]) {
        const targetName = name + "Target";
        this[targetName].textContent = "";
        this[targetName].hidden = true;
      }
    }
  }

  _showFieldError(targetName, message) {
    const has = "has" + targetName.charAt(0).toUpperCase() + targetName.slice(1) + "Target";
    if (!this[has]) return;
    const el = this[targetName + "Target"];
    el.textContent = message;
    el.hidden = false;
  }

  _applyFieldErrors(error) {
    const details = error instanceof APIError ? error.details : null;
    const fields = details && Array.isArray(details.fields) ? details.fields : [];
    let matched = false;
    for (const f of fields) {
      const fieldName = (f.field || "").toString();
      if (fieldName === "amount") {
        this._showFieldError("amountError", f.message || "Montant invalide.");
        matched = true;
      } else if (fieldName === "clientName" || fieldName === "client_name") {
        this._showFieldError("clientNameError", f.message || "Nom du client invalide.");
        matched = true;
      } else if (fieldName === "notes") {
        this._showFieldError("notesError", f.message || "Description invalide.");
        matched = true;
      }
    }
    if (!matched) {
      const codeRequired = fields.some((f) => f.code === "client_name_required_above_76");
      if (codeRequired) {
        this._showFieldError(
          "clientNameError",
          "Le nom du client est obligatoire pour les ventes ≥ 76 € TTC.",
        );
        matched = true;
      }
    }
    if (!matched) {
      const msg = (error instanceof APIError && error.message) || "Données invalides.";
      this._showErrorBanner("Données invalides", msg);
    }
  }

  _formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  }
}
